<script setup lang="ts">
import type {SwadeItemInterface, SwadeItemContainerInterface} from "@/types/items.ts";
import {useAppDataStore} from "@/stores/app-data.ts";
import {isUniqueContainer, useCharacterDataStore} from "@/stores/character-data.ts";
import {useUxData} from "@/stores/ux-data.ts";
import {itemUnitCost} from "@/appdata/item-cost.ts";
import {computed, ref, watch} from "vue";

const store = useAppDataStore();
const swade = useCharacterDataStore();
const ux = useUxData();

const emit = defineEmits([ 'finished' ])
const props = defineProps<{
  container?: SwadeItemContainerInterface | null
}>()

const character = defineModel<any>('character');
const activeCharacter = computed(() => character.value ?? store.character);

// The container the money is for: the one an add just filled, or one just removed (spec 2026-10-13
// §7). With none — the Adjust button's funds-only form — there is no item and no quantity.
const item = computed<SwadeItemInterface | null>(() => props.container?.selection ?? null);

// A unique container is one unit (spec 2026-10-13 §7): a copy of an item that asks, or one a pick has
// customised. A player who wants three adds three times, so it offers no quantity.
const offersQuantity = computed(() => !!item.value && !isUniqueContainer(activeCharacter.value, props.container));

const quantity = ref(1);
const amount = ref(0);

// Both inputs are controlled now. The amount used to be read straight out of the DOM with
// querySelector on click, which cannot express "quantity multiplies cost" and left the two fields
// unable to see each other.
const unitCost = computed(() => itemUnitCost(item.value));
const resetFromItem = () => {
  quantity.value = 1;
  amount.value = unitCost.value;
};
watch(item, resetFromItem, { immediate: true });

// Quantity drives the amount, but does not own it: a manual edit to the amount survives until the
// quantity is touched again, so haggling over the price still works.
watch(quantity, (value) => {
  const units = Math.max(1, Math.floor(Number(value) || 1));
  if (units !== value) quantity.value = units;
  amount.value = unitCost.value * units;
});

// Both hosts open this modal only after the add has put one unit into the container, so "falls
// through to the item" adds the rest of the units typed here to that container — the one on the sheet
// with this uuid, never another copy of the item. A removed container is no longer on the sheet, and
// nothing is written.
const applyQuantity = async () => {
  const uuid = props.container?.uuid;
  const live = (activeCharacter.value?.equipment ?? [])
      .find((entry: SwadeItemContainerInterface) => entry.uuid === uuid) ?? null;
  if (!activeCharacter.value || !uuid || !live) return;

  await swade.setContainerQuantity(activeCharacter.value, live, (live.quantity ?? 1) + quantity.value - 1);
};

// The money the adjuster can move. A wealth die ('d6') or anything else that is not a number has
// no amount to add to or take away, so it is left alone (owner's ruling 2026-10-09).
const countableFunds = (value: unknown): number | null => {
  const funds = parseFloat(String(value ?? 0));
  return Number.isNaN(funds) ? null : funds;
};

// v-model.number leaves a cleared input as '', so a cleared amount moves no money.
const adjustment = computed(() => Number(amount.value) || 0);

const onIncreaseClick = async (event: any) => {
  console.info("CurrencyAdjuster | onIncreaseClick")
  const target = activeCharacter.value;
  const currentFunds = countableFunds(target?.purchasingPower);
  if (target && currentFunds !== null) {
    target.purchasingPower = currentFunds + adjustment.value;
  }

  await applyQuantity();
  emit('finished', event);
}

const onDecreaseClick = async (event: any) => {
  console.info("CurrencyAdjuster | onDecreaseClick")
  const target = activeCharacter.value;
  const currentFunds = countableFunds(target?.purchasingPower);
  if (target && currentFunds !== null) {
    console.debug("CurrencyAdjuster | onDecreaseClick | ", adjustment.value, currentFunds)
    if ((currentFunds >= adjustment.value) || confirm(ux.labels.messages.confirmCurrencyAdjustment)) {
      let adjustedAmount = currentFunds - adjustment.value;
      if (adjustedAmount < 0) {
        adjustedAmount = 0;
      }
      target.purchasingPower = adjustedAmount;
    }
  }

  await applyQuantity();
  emit('finished', event);
}

// Free still carries the quantity: the player is taking N of them, they are just not paying.
const onFreeClick = async (event: any) => {
  console.info("CurrencyAdjuster | onFreeClick")

  await applyQuantity();
  emit('finished', event);
}

</script>

<template>
  <div class="modal-backdrop fade show"></div>
  <div id="currency-adjuster" class="modal fade d-block show" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header header character-options">{{ ux.labels.tools.modalCurrencyAdjuster.header }}</div>
        <div class="modal-body">
          <div v-if="item" class="form-group mb-3">
            <label class="required">{{ item.name }}</label>
            <div class="summary" v-html="item.summary"></div>
          </div>
          <hr v-if="item"/>

          <div class="input-group">
            <label class="required input-group-text">{{ ux.labels.tools.modalCurrencyAdjuster.amount.label }}</label>
            <input v-if="offersQuantity"
                   id="currency-adjuster-quantity"
                   v-model.number="quantity"
                   type="number" class="form-control adjustment-quantity"
                   min="1" step="1" />
            <label v-if="offersQuantity"
                   class="input-group-text" for="currency-adjuster-quantity">x</label>
            <input v-model.number="amount"
                   type="number" class="form-control adjustment-amount"
                   min="0" />
            <div class="input-group-text text-end">
              <button type="button" class="btn btn-sm btn-warning"
                      data-bs-toggle="tooltip"
                      data-bs-placement="top"
                      :title="ux.labels.tools.modalCurrencyAdjuster.decrease.label"
                      @click="onDecreaseClick"
              >
                <span class="fa-light fa-minus-hexagon"></span>
                <span class="sr-only">{{ ux.labels.tools.modalCurrencyAdjuster.decrease.label }}</span>
              </button>
              <button type="button" class="btn btn-sm btn-success"
                      data-bs-toggle="tooltip"
                      data-bs-placement="top"
                      :title="ux.labels.tools.modalCurrencyAdjuster.increase.label"
                      @click="onIncreaseClick"
              >
                <span class="fa-light fa-plus-hexagon"></span>
                <span class="sr-only">{{ ux.labels.tools.modalCurrencyAdjuster.increase.label }}</span>
              </button>
              <button v-if="item" type="button" class="btn btn-sm btn-light" @click="onFreeClick">
                {{ ux.labels.tools.modalCurrencyAdjuster.free.label }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
